import { useState } from "react";
import { CheckCircle, Check, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { trackFormSubmit } from "@/lib/analytics";
import { API } from "@/lib/api";

const AI_AREAS = [
  "Simulación de resultados",
  "Análisis de piel/imagen",
  "Chatbots",
  "Marketing automatizado",
  "Recomendación tratamientos",
];

const INITIAL_FORM = {
  clinica: "", contacto: "", email: "", cargo: "", telefono: "", ciudad: "",
  usa_ia: "", areas_ia: [], herramientas: "", datos_pacientes: "",
  informa_paciente: "", servicio_interes: "", urgencia: "", observaciones: "",
  acepta_lopd: false,
};

export function DiagnosticoForm({ onOpenPrivacy }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAreaToggle = (area) => {
    setForm(prev => ({
      ...prev,
      areas_ia: prev.areas_ia.includes(area)
        ? prev.areas_ia.filter(a => a !== area)
        : [...prev.areas_ia, area],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.clinica || !form.contacto || !form.email) {
      const missing = [];
      if (!form.clinica) missing.push("Nombre de la clínica");
      if (!form.contacto) missing.push("Persona de contacto");
      if (!form.email) missing.push("Email");
      toast.error(`Faltan campos obligatorios: ${missing.join(", ")}`);
      return;
    }
    if (!form.acepta_lopd) {
      toast.error("Debes aceptar la política de privacidad");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API}/diagnostico`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      let data;
      try { data = await response.json(); } catch { data = {}; }
      if (!response.ok) {
        toast.error(data.detail || `Error del servidor (${response.status})`);
        return;
      }
      trackFormSubmit("diagnostico");
      toast.success("Solicitud enviada correctamente. Te contactaremos en 24-48h.");
      setForm(INITIAL_FORM);
    } catch (error) {
      toast.error("No se pudo conectar con el servidor. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-4 max-w-4xl mx-auto mb-12">
        <div className="flex-1 h-px bg-gray-700"></div>
        <span className="text-gray-500 text-sm">O si prefieres darnos más detalles</span>
        <div className="flex-1 h-px bg-gray-700"></div>
      </div>

      <div className="text-center mb-10">
        <span className="eyebrow mb-4 inline-flex" style={{ background: 'rgba(16, 185, 129, 0.15)', borderColor: 'rgba(16, 185, 129, 0.3)', color: '#34D399' }}>
          <CheckCircle className="w-4 h-4" />
          <span>Diagnóstico completo</span>
        </span>
        <h2 className="section-title mt-4">Solicita un diagnóstico detallado</h2>
        <p className="section-subtitle mx-auto mt-4">
          Si ya sabes qué herramientas usas y quieres una valoración más precisa, completa el formulario completo.
        </p>
      </div>

      <div className="form-container max-w-4xl mx-auto">
        <div className="form-header">
          <h3 className="text-xl font-bold text-white mb-2">Formulario de diagnóstico</h3>
          <p className="text-gray-400 text-sm">Los campos marcados con * son obligatorios</p>
        </div>

        <form onSubmit={handleSubmit} className="form-body">
          <div className="grid md:grid-cols-2 gap-5 mb-8">
            <div>
              <label className="form-label">Nombre de la clínica *</label>
              <Input type="text" value={form.clinica} onChange={(e) => handleChange('clinica', e.target.value)} placeholder="Ej: Clínica Estética Avanzada" className="form-input" required data-testid="input-clinica" />
            </div>
            <div>
              <label className="form-label">Persona de contacto *</label>
              <Input type="text" value={form.contacto} onChange={(e) => handleChange('contacto', e.target.value)} placeholder="Nombre y apellidos" className="form-input" required data-testid="input-contacto" />
            </div>
            <div>
              <label className="form-label">Correo electrónico *</label>
              <Input type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="correo@clinica.es" className="form-input" required data-testid="input-email" />
            </div>
            <div>
              <label className="form-label">Teléfono</label>
              <Input type="tel" value={form.telefono} onChange={(e) => handleChange('telefono', e.target.value)} placeholder="+34 600 000 000" className="form-input" data-testid="input-telefono" />
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 mb-8">
            <h4 className="text-white font-semibold mb-6">Uso de inteligencia artificial</h4>
            
            <div className="grid md:grid-cols-2 gap-5 mb-6">
              <div>
                <label className="form-label">¿Utilizan herramientas de IA?</label>
                <Select value={form.usa_ia} onValueChange={(v) => handleChange('usa_ia', v)}>
                  <SelectTrigger className="form-input" data-testid="select-usa-ia"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="si">Sí</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="no_seguro">No estamos seguros</SelectItem>
                    <SelectItem value="valorando">Lo estamos valorando</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="form-label">Urgencia</label>
                <Select value={form.urgencia} onValueChange={(v) => handleChange('urgencia', v)}>
                  <SelectTrigger className="form-input" data-testid="select-urgencia"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="baja">Baja</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mb-6">
              <label className="form-label mb-3">Áreas donde usan o prevén usar IA</label>
              <div className="checkbox-grid">
                {AI_AREAS.map((area) => (
                  <label key={area} className="checkbox-item">
                    <Checkbox checked={form.areas_ia.includes(area)} onCheckedChange={() => handleAreaToggle(area)} data-testid={`checkbox-area-${area.toLowerCase().replace(/\s+/g, '-')}`} />
                    <span>{area}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="form-label">Herramientas que utilizan</label>
              <Textarea value={form.herramientas} onChange={(e) => handleChange('herramientas', e.target.value)} placeholder="Ej: Simuladores 3D, ChatGPT, CRMs..." className="form-input min-h-[80px]" data-testid="textarea-herramientas" />
            </div>
          </div>

          <div className="p-5 rounded-xl border border-gray-700 bg-gray-800/30 mb-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={form.acepta_lopd} onCheckedChange={(checked) => handleChange('acepta_lopd', checked)} className="mt-1" data-testid="checkbox-lopd" />
              <span className="text-gray-300 text-sm leading-relaxed">
                He leído y acepto la <button type="button" onClick={onOpenPrivacy} className="text-blue-400 underline">política de privacidad</button> y consiento el tratamiento de mis datos para gestionar mi solicitud. *
              </span>
            </label>
          </div>

          <Button type="submit" className="btn-primary w-full py-4 text-base" disabled={loading} data-testid="form-submit-button">
            {loading ? (<><Loader2 className="w-5 h-5 animate-spin" />Enviando...</>) : (<><Send className="w-5 h-5" />Solicitar diagnóstico</>)}
          </Button>

          <p className="text-center text-gray-500 text-xs mt-4">
            Valoración inicial orientativa. No constituye asesoramiento jurídico definitivo.
          </p>
        </form>
      </div>
    </>
  );
}
